/**
 * GPU port of windField.compose() as a TSL node graph.
 *
 * Mirrors the CPU code term by term (steering, Holland tangential with the
 * height factor, Stokes-streamfunction inflow and updraft, curl noise with
 * the envelope inside the curl) with the same branch conditions, so the
 * CPU reference and the GPU path evaluate the same function. Differences
 * should come only from float32 arithmetic. gpuTracers validation measures
 * how large those differences are.
 *
 * Scalars that depend only on time or parameters are computed on the CPU
 * in float64 and uploaded as uniforms:
 * - storm center at each RK2 stage time,
 * - turbulence phases (phi_m + omega_m t) mod 2 pi, which keeps the
 *   argument of cos/sin small even after days of storm time.
 */
import { Vector2, Vector3, type Node } from 'three/webgpu';
import { cos, exp, float, max, pow, select, sin, sqrt, uniform, vec3 } from 'three/tsl';
import { HOLLAND_MIN_RADIUS_M } from '../../config/constants';
import { clampB } from '../hollandProfile';
import type { WindField } from '../windField';

type V3 = Node<'vec3'>;
type F = Node<'float'>;

// Uniform node types, inferred from factories so they track @types/three.
const mkNum = () => uniform(0);
const mkV2 = () => uniform(new Vector2());
const mkV3 = () => uniform(new Vector3());
export type UNum = ReturnType<typeof mkNum>;
export type UV2 = ReturnType<typeof mkV2>;
type UV3 = ReturnType<typeof mkV3>;

/** Per-RK2-stage uniforms: storm center and turbulence phases at that time. */
export interface WindStageUniforms {
  center: UV2;
  phases: UNum[];
  /** Write values for time tS. Allocation-free. */
  set(tS: number): void;
}

export interface WindTsl {
  /** Build the wind expression at position p for a stage. Call while building a kernel. */
  sample(p: V3, stage: WindStageUniforms): V3;
  /** Create uniforms for one RK2 stage time. */
  createStage(): WindStageUniforms;
  /** Copy current CPU parameters (Holland, inflow, noise, gust, toggles) into uniforms. */
  syncParams(): void;
}

const TWO_PI = 2 * Math.PI;

export function createWindTsl(field: WindField): WindTsl {
  const h = field.holland;
  const tr = field.translation;

  // Holland
  const uPc = uniform(0);
  const uDp = uniform(0);
  const uRmax = uniform(1);
  const uB = uniform(1);
  const uRho = uniform(1);
  const uFAbs = uniform(0);
  const uSign = uniform(1);
  // Height factor
  const uFullH = uniform(1e30);
  const uTop = uniform(1e30);
  // Inflow
  const uInflowOn = uniform(0);
  const uU0 = uniform(0);
  const uInflowDepth = uniform(1);
  // Steering
  const uSteer = uniform(new Vector2());
  // Noise
  const modes = field.noise ? field.noise.modes() : null;
  const nModes = modes ? modes.count : 0;
  const uK: UV3[] = [];
  const uA: UV3[] = [];
  const uC: UV3[] = [];
  for (let m = 0; m < nModes; m++) {
    uK.push(mkV3());
    uA.push(mkV3());
    uC.push(mkV3());
  }
  const uNoiseAmp = uniform(0); // gain * amplitude * velNorm, 0 when disabled
  const uRPeak = uniform(1);
  const uNoiseH = uniform(1);

  function syncParams(): void {
    uPc.value = h.pCentralPa;
    uDp.value = h.pAmbientPa - h.pCentralPa;
    uRmax.value = h.rMaxM;
    uB.value = clampB(h.B);
    uRho.value = h.airDensity;
    uFAbs.value = field.coriolis.fAbs;
    uSign.value = field.coriolis.sign;
    uSteer.value.set(tr.steerXMps, tr.steerZMps);

    const inflow = field.inflow;
    if (inflow) {
      uFullH.value = field.tangentialFullHeightM;
      uTop.value = inflow.params.topM;
      uInflowOn.value = field.enabled.inflow ? 1 : 0;
      uU0.value = inflow.u0;
      uInflowDepth.value = inflow.params.inflowDepthM;
    } else {
      uFullH.value = 1e30;
      uTop.value = 1e30;
      uInflowOn.value = 0;
    }

    const noise = field.noise;
    if (noise && modes) {
      const md = noise.modes();
      for (let m = 0; m < nModes; m++) {
        (uK[m] as UV3).value.set(md.kx[m] as number, md.ky[m] as number, md.kz[m]);
        (uA[m] as UV3).value.set(md.ax[m] as number, md.ay[m] as number, md.az[m]);
        (uC[m] as UV3).value.set(md.cx[m] as number, md.cy[m] as number, md.cz[m]);
      }
      const on = field.enabled.turbulence && field.gust > 0 ? 1 : 0;
      uNoiseAmp.value = on * field.gust * noise.params.amplitudeMps * md.velNorm;
      uRPeak.value = noise.params.rPeakM;
      uNoiseH.value = noise.params.heightScaleM;
    } else {
      uNoiseAmp.value = 0;
    }
  }
  syncParams();

  function createStage(): WindStageUniforms {
    const center = mkV2();
    const phases: UNum[] = [];
    for (let m = 0; m < nModes; m++) phases.push(mkNum());
    return {
      center,
      phases,
      set(tS: number): void {
        center.value.set(tr.originXM + tr.steerXMps * tS, tr.originZM + tr.steerZMps * tS);
        if (modes) {
          for (let m = 0; m < nModes; m++) {
            const theta = (modes.phase[m] as number) + (modes.omega[m] as number) * tS;
            (phases[m] as UNum).value = theta - TWO_PI * Math.floor(theta / TWO_PI);
          }
        }
      },
    };
  }

  function sample(p: V3, stage: WindStageUniforms): V3 {
    const dx = p.x.sub(stage.center.x);
    const y = p.y;
    const dz = p.z.sub(stage.center.y);
    const r = sqrt(dx.mul(dx).add(dz.mul(dz)));
    const tooClose = r.lessThan(HOLLAND_MIN_RADIUS_M);
    const rs = max(r, float(HOLLAND_MIN_RADIUS_M));

    // Holland speed (hollandSpeedWithF)
    const x = pow(uRmax.div(rs), uB);
    const cyc = uB.div(uRho).mul(uDp).mul(x).mul(exp(x.negate()));
    const half = rs.mul(uFAbs).mul(0.5);
    const vRaw = max(sqrt(cyc.add(half.mul(half))).sub(half), 0);
    // Height factor: 1 below fullH, linear to 0 at the lid.
    const hf: F = select(
      y.lessThanEqual(uFullH),
      float(1),
      select(y.greaterThanEqual(uTop), float(0), uTop.sub(y).div(uTop.sub(uFullH))),
    );
    const kT = select(tooClose, float(0), uSign.mul(vRaw).mul(hf).div(rs));
    let u: V3 = vec3(uSteer.x.add(kT.mul(dz)), float(0), uSteer.y.sub(kT.mul(dx)));

    // Inflow / updraft (inflowUpdraft.sample)
    const hIn = uInflowDepth;
    const s = rs.div(uRmax);
    const s2 = s.mul(s);
    const e4 = exp(s2.mul(s2).negate());
    const R = float(1).sub(e4);
    const dRdr = s2.mul(s).mul(e4).mul(4).div(uRmax);
    const ey = exp(y.negate().div(hIn));
    const lid = float(1).sub(y.div(uTop));
    const Y = float(1).sub(ey).mul(lid);
    const dYdy = ey.div(hIn).mul(lid).sub(float(1).sub(ey).div(uTop));
    const inflowMask = select(
      tooClose.or(y.lessThanEqual(0)).or(y.greaterThanEqual(uTop)).or(uU0.equal(0)),
      float(0),
      uInflowOn,
    );
    const ur = uU0.mul(R).mul(dYdy).negate().div(rs).mul(inflowMask);
    const w = uU0.mul(dRdr).mul(Y).div(rs).mul(inflowMask);
    const urOverR = select(tooClose, float(0), ur.div(rs));
    u = u.add(vec3(urOverR.mul(dx), w, urOverR.mul(dz)));

    // Turbulence (curlNoise.addTo)
    if (nModes > 0) {
      const rp2 = uRPeak.mul(uRPeak);
      const sN2 = dx.mul(dx).add(dz.mul(dz)).div(rp2);
      const eSq = exp(float(1).sub(sN2));
      const er = sN2.mul(eSq);
      const yPos = y.greaterThan(0);
      const eyN = exp(max(y, 0).negate().div(uNoiseH));
      const e = er.mul(eyN);
      const dEr = eSq.mul(float(1).sub(sN2));
      const gx = dEr.mul(dx.mul(2).div(rp2)).mul(eyN);
      const gz = dEr.mul(dz.mul(2).div(rp2)).mul(eyN);
      const gy = select(yPos, er.mul(eyN).negate().div(uNoiseH), float(0));

      let cu: V3 = vec3(0, 0, 0);
      let psi: V3 = vec3(0, 0, 0);
      for (let m = 0; m < nModes; m++) {
        const k = uK[m] as UV3;
        const theta = k.x
          .mul(dx)
          .add(k.y.mul(y))
          .add(k.z.mul(dz))
          .add(stage.phases[m] as UNum);
        cu = cu.add((uC[m] as UV3).mul(cos(theta)));
        psi = psi.add((uA[m] as UV3).mul(sin(theta)));
      }
      // curl(e psi) = e curl(psi) + grad(e) x psi
      const g = vec3(gx, gy, gz);
      const turb = cu.mul(e).add(g.cross(psi)).mul(uNoiseAmp);
      const noiseMask = select(e.lessThan(1e-6).or(uNoiseAmp.equal(0)), float(0), float(1));
      u = u.add(turb.mul(noiseMask));
    }
    return u;
  }

  return { sample, createStage, syncParams };
}
